import { ReceivableCorrection, ReceivableInvoice, ReceivablePayment, ReceivableRow } from '@bariendo/lib/receivables';
import { useMedplum } from '@medplum/react';
import { useMemo } from 'react';
import { alert } from '@/services/alert';
import { fetchBotRead, fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { formatLedgerMoney } from '../ledger/ledgerRows';
import { BILL_TO_LABEL } from './receivableRows';

export interface ReceivableActions {
  nextInvoiceNumber: (row: ReceivableRow) => Promise<string>;
  markInvoiced: (row: ReceivableRow, invoice: ReceivableInvoice) => Promise<void>;
  recordPayment: (row: ReceivableRow, payment: ReceivablePayment) => Promise<void>;
  saveNote: (row: ReceivableRow, note: string | null) => Promise<void>;
  correct: (row: ReceivableRow, correction: ReceivableCorrection) => Promise<void>;
}

/** The line as it stands, as a correction: Undo resaves it (BAR-3104). */
function asCorrection(row: ReceivableRow): ReceivableCorrection {
  return {
    invoicedOn: row.invoicedOn ?? '',
    invoicedAmount: row.invoicedAmount ?? 0,
    invoiceNumber: row.invoiceNumber ?? '',
    ...(row.status === 'PAID'
      ? { paidOn: row.paidOn ?? '', paidAmount: row.paidAmount ?? 0, bankReference: row.bankReference ?? '' }
      : {}),
  };
}

const message = (error: unknown) => (error instanceof Error ? error.message : 'unknown error');

/** Finance's actions on a receivable (BAR-3040), each with the prototype's toast and Undo. */
export function useReceivableActions(reload: () => void): ReceivableActions {
  const medplum = useMedplum();

  return useMemo(() => {
    // A failed write re-reads the lines: one that timed out may still have landed (BAR-3125).
    const call = <T>(input: Record<string, unknown>) =>
      fetchBotWrite<T>(medplum, 'create-treatment-payment', input, reload);

    const undo = (operation: string, row: ReceivableRow, what: string) => () => {
      call({ operation, receivableId: row.id })
        .then(reload)
        .catch((error: unknown) => alert.error(`Couldn't undo ${what}: ${message(error)}`));
    };

    return {
      nextInvoiceNumber: async (row) =>
        (
          await fetchBotRead<{ invoiceNumber: string }>(medplum, 'create-treatment-payment', {
            operation: 'nextInvoiceNumber',
            billTo: row.billTo,
          })
        ).invoiceNumber,

      markInvoiced: async (row, invoice) => {
        await call({ operation: 'markReceivableInvoiced', receivableId: row.id, receivableInvoice: invoice });
        reload();
        showUndoToast(
          `Invoice ${invoice.invoiceNumber} sent to ${BILL_TO_LABEL[row.billTo]}`,
          undo('undoReceivableInvoiced', row, 'the invoice')
        );
      },

      recordPayment: async (row, payment) => {
        await call({ operation: 'recordReceivablePayment', receivableId: row.id, receivablePayment: payment });
        reload();
        showUndoToast(
          `Payment recorded · added to Revenue as ${formatLedgerMoney(payment.paidAmount)}`,
          undo('undoReceivablePayment', row, 'the payment')
        );
      },

      saveNote: async (row, note) => {
        await call({ operation: 'setReceivableNote', receivableId: row.id, note });
        reload();
      },

      correct: async (row, correction) => {
        await call({ operation: 'correctReceivable', receivableId: row.id, receivableCorrection: correction });
        reload();
        const previous = asCorrection(row);
        showUndoToast(
          `${correction.invoiceNumber} updated${row.status === 'PAID' ? ' · Revenue row updated' : ''}`,
          () => {
            call({ operation: 'correctReceivable', receivableId: row.id, receivableCorrection: previous })
              .then(reload)
              .catch((error: unknown) => alert.error(`Couldn't undo the edit: ${message(error)}`));
          }
        );
      },
    };
  }, [medplum, reload]);
}
